<template>
  <div class="equipment-manager">
    <!-- Add Equipment Dialog -->
    <EquipmentFormDialog
      v-model="showEquipmentDialog"
      :form-data="newEquipment"
      :selected-space="selectedSpace"
      :selected-equipment="selectedEquipment"
      :equipment-template-options="equipmentTemplateOptions"
      :equipment-brick-classes="equipmentBrickClasses"
      :space-options="spaceOptions"
      :available-spaces-for-zone="availableSpacesForZone"
      :loading-templates="loadingTemplates"
      :saving="saving"
      @update:name="newEquipment.name = $event"
      @update:brick-class="newEquipment.brick_class = $event"
      @update:space-id="newEquipment.space_id = $event"
      @update:description="newEquipment.description = $event"
      @update:serves-space="newEquipment.serves_space = $event"
      @template-select="onEquipmentTemplateSelect"
      @submit="addEquipment"
    />

    <!-- Edit Equipment Dialog -->
    <EditEquipmentDialog
      v-model="showEditEquipmentDialog"
      :form-data="editingEquipment"
      :equipment-brick-classes="equipmentBrickClasses"
      :available-ddc-panels="availableDdcPanels"
      :power-source-options="powerSourceOptions"
      :controller-templates="controllerTemplates"
      :ddc-controller-to-add="ddcControllerToAdd"
      :total-io-capacity="calculateTotalIoForDdc()"
      :available-spaces-for-zone="availableSpacesForZone"
      :saving="saving"
      :is-templated-equipment="isTemplatedEquipment"
      :is-v-a-v-or-terminal-unit="isVAVOrTerminalUnit"
      @update:name="editingEquipment.name = $event"
      @update:brick-class="editingEquipment.brick_class = $event"
      @update:description="editingEquipment.description = $event"
      @update:controller-id="editingEquipment.controller_id = $event"
      @update:power-source-id="editingEquipment.power_source_id = $event"
      @update:power-classification="editingEquipment.power_classification = $event"
      @update:serves-space="editingEquipment.serves_space = $event"
      @update:ddc-template-id="ddcControllerToAdd.template_id = $event"
      @update:ddc-quantity="ddcControllerToAdd.quantity = $event"
      @update:ddc-position-label="ddcControllerToAdd.position_label = $event"
      @open-ddc-allocation-dialog="emit('open-ddc-allocation-dialog')"
      @open-power-connection-dialog="emit('open-power-connection-dialog')"
      @load-controller-templates="loadControllerTemplates"
      @remove-controller-from-ddc="removeControllerFromDdc"
      @add-controller-to-ddc="addControllerToDdc"
      @submit="updateEquipment"
    />

    <!-- Add Device Dialog -->
    <DeviceFormDialog
      v-model="showDeviceDialog"
      :form-data="newDevice"
      :selected-equipment="selectedEquipment"
      :all-template-options="allTemplateOptions"
      :device-brick-classes="deviceBrickClasses"
      :device-location-path="deviceLocationPath"
      :saving="saving"
      @update:name="newDevice.name = $event"
      @update:brick-class="newDevice.brick_class = $event"
      @update:device-part-id="newDevice.device_part_id = $event"
      @update:location-description="newDevice.location_description = $event"
      @template-select="onDeviceTemplateSelect"
      @open-location-picker="openLocationPicker"
      @clear-location="clearDeviceLocation"
      @submit="addDevice"
    />

    <!-- Edit Device Dialog -->
    <EditDeviceDialog
      v-model="showEditDeviceDialog"
      :form-data="editingDevice"
      :device-templates="deviceTemplates"
      :device-brick-classes="filteredDeviceBrickClasses"
      :device-location-path="deviceLocationPath"
      :saving="saving"
      :is-templated-device="isTemplatedDevice"
      :template-brick-class-constraint="editingDeviceTemplateConstraint"
      @update:name="editingDevice.name = $event"
      @update:brick-class="editingDevice.brick_class = $event"
      @update:serial-number="editingDevice.serial_number = $event"
      @update:device-part-id="editingDevice.device_part_id = $event"
      @update:location-description="editingDevice.location_description = $event"
      @template-select="onEditDeviceTemplateSelect"
      @open-location-picker="openLocationPicker('edit')"
      @clear-location="clearDeviceLocation('edit')"
      @submit="updateDevice"
    />

    <!-- Add Point Dialog -->
    <PointFormDialog
      v-model="showPointDialog"
      :form-data="newPoint"
      :selected-equipment="selectedEquipment"
      :point-brick-classes="pointBrickClasses"
      :saving="saving"
      :infer-quantity-kind="inferQuantityKind"
      @update:name="newPoint.name = $event"
      @update:brick-class="newPoint.brick_class = $event"
      @update:unit-mapping-id="newPoint.unit_mapping_id = $event"
      @submit="addPoint"
    />

    <!-- Edit Point Dialog -->
    <EditPointDialog
      v-model="showEditPointDialog"
      :form-data="editingPoint"
      :point-brick-classes="pointBrickClasses"
      :point-type-options="pointTypeOptions"
      :saving="saving"
      :infer-quantity-kind="inferQuantityKind"
      @update:name="editingPoint.name = $event"
      @update:brick-class="editingPoint.brick_class = $event"
      @update:point-type="editingPoint.point_type = $event"
      @update:unit-mapping-id="editingPoint.unit_mapping_id = $event"
      @submit="updatePoint"
    />

    <!-- Add Zone Section Dialog -->
    <AddZoneSectionDialog
      v-model="showZoneSectionDialog"
      :form-data="newZoneSection"
      :selected-ahu="selectedAHU"
      :zone-templates="zoneTemplates"
      :selected-zone-template="selectedZoneTemplate"
      :zone-section-type-options="zoneSectionTypeOptions"
      :available-spaces-for-zone="availableSpacesForZone"
      @update:name="newZoneSection.name = $event"
      @update:zone-type="newZoneSection.zone_type = $event"
      @update:serves-space="newZoneSection.serves_space = $event"
      @apply-zone-template="applyZoneTemplate"
      @reset="resetZoneSectionDialog"
      @submit="addZoneSection"
    />

    <!-- Location Picker Dialog -->
    <LocationPickerDialog
      v-model="showLocationPicker"
      :location-tree-nodes="locationTreeNodes"
      :selected-location-node="selectedLocationNode"
      :selected-location-for-picker="selectedLocationForPicker"
      :selected-location-label="getSelectedLocationLabel()"
      @update:selected-location="selectedLocationNode = $event"
      @confirm-selection="confirmLocationSelection"
    />
  </div>
</template>

<script setup>
  import { ref, computed, inject } from 'vue';
  import { useQuasar } from 'quasar';
  import { authenticatedFetch } from '@/utils/apiHelper';
  import { useBrickClassOptions, useTemplateProtection } from '@/composables/brick-schema';
  import { useEquipmentApi, useDeviceApi, usePointApi, useTemplateApi } from '@/composables/brick-schema/api';
  import { useBrickValidation } from '@/composables/brick-schema/operations';
  import { useNodeTypeHelpers } from '@/composables/brick-schema/logic';
  import quoteService from '@/services/quoteService';

  // Import dialog components
  import EquipmentFormDialog from '../dialogs/EquipmentFormDialog.vue';
  import EditEquipmentDialog from '../dialogs/EditEquipmentDialog.vue';
  import DeviceFormDialog from '../dialogs/DeviceFormDialog.vue';
  import EditDeviceDialog from '../dialogs/EditDeviceDialog.vue';
  import PointFormDialog from '../dialogs/PointFormDialog.vue';
  import EditPointDialog from '../dialogs/EditPointDialog.vue';
  import AddZoneSectionDialog from '../dialogs/equipment/AddZoneSectionDialog.vue';
  import LocationPickerDialog from '../dialogs/LocationPickerDialog.vue';

  // Props
  const props = defineProps({
    siteId: {
      type: [String, Number],
      required: true,
    },
    localHierarchyData: {
      type: Object,
      default: () => ({}),
    },
    equipmentList: {
      type: Array,
      default: () => [],
    },
    quoteContext: {
      type: Object,
      default: null,
    },
  });

  // Emits
  const emit = defineEmits([
    'refresh-data',
    'equipment-added',
    'equipment-updated',
    'equipment-deleted',
    'device-added',
    'device-updated',
    'device-deleted',
    'point-added',
    'point-updated',
    'point-deleted',
    'zone-section-added',
    'open-ddc-allocation-dialog',
    'open-power-connection-dialog',
  ]);

  // Inject shared context from parent
  const schemaContext = inject('schemaContext', null);

  // Quasar
  const $q = useQuasar();

  // Composables
  const {
    equipmentBrickClasses,
    deviceBrickClasses,
    pointBrickClasses,
    zoneSectionTypeOptions,
    getFilteredDeviceBrickClasses,
  } = useBrickClassOptions();

  const {
    isTemplatedEquipment,
    isTemplatedDevice,
    isTemplatedPoint,
    canAddPointsToEquipment,
  } = useTemplateProtection();

  const {
    isVAVOrTerminalUnit,
  } = useNodeTypeHelpers();

  const {
    inferQuantityKind,
  } = useBrickValidation();

  const siteIdRef = computed(() => props.siteId);
  const equipmentApi = useEquipmentApi(siteIdRef);
  const deviceApi = useDeviceApi(siteIdRef);
  const pointApi = usePointApi(siteIdRef);
  const templateApi = useTemplateApi(siteIdRef);

  // State from composables
  const saving = ref(false);
  const loadingTemplates = ref(false);

  // ===================================
  // DIALOG VISIBILITY STATES
  // ===================================
  const showEquipmentDialog = ref(false);
  const showEditEquipmentDialog = ref(false);
  const showDeviceDialog = ref(false);
  const showEditDeviceDialog = ref(false);
  const showPointDialog = ref(false);
  const showEditPointDialog = ref(false);
  const showZoneSectionDialog = ref(false);
  const showLocationPicker = ref(false);

  // ===================================
  // FORM DATA
  // ===================================
  const newEquipment = ref({
    name: '',
    brick_class: '',
    description: '',
    equipment_template_id: null,
    selectedTemplate: null,
    space_id: null,
    parent_equipment_id: null,
    serves_space: null,
  });

  const editingEquipment = ref({
    id: null,
    name: '',
    description: '',
    brick_class: '',
    serves_space: null,
    controller_id: null,
    power_source_id: null,
    power_classification: null,
    ddcControllers: [],
  });

  const newDevice = ref({
    name: '',
    brick_class: '',
    device_part_id: null,
    device_template_id: null,
    selectedTemplate: null,
    properties: {},
    location_space_id: null,
    location_description: '',
  });

  const editingDevice = ref({
    id: null,
    name: '',
    brick_class: '',
    serial_number: '',
    device_part_id: null,
    device_part: null,
    properties: {},
    location_space_id: null,
    location_description: '',
  });

  // Track template's brick_class constraint for subclass filtering
  const editingDeviceTemplateConstraint = ref(null);

  // Computed: filter device brick classes based on template constraint
  const filteredDeviceBrickClasses = computed(() => {
    if (!editingDeviceTemplateConstraint.value) {
      return deviceBrickClasses;
    }
    return getFilteredDeviceBrickClasses(editingDeviceTemplateConstraint.value);
  });

  const newPoint = ref({
    name: '',
    brick_class: '',
    point_type: 'Virtual',
    unit_mapping_id: null,
  });

  const editingPoint = ref({
    id: null,
    name: '',
    brick_class: '',
    point_type: 'Virtual',
    unit_mapping_id: null,
  });

  const newZoneSection = ref({
    name: '',
    zone_type: 'mixing',
    serves_space: null,
    brick_class: 'AHU_Zone_Section',
  });

  // ===================================
  // SELECTION STATE
  // ===================================
  const selectedSpace = ref(null);
  const selectedEquipment = ref(null);
  const selectedAHU = ref(null);
  const selectedZoneTemplate = ref(null);

  // ===================================
  // TEMPLATE DATA
  // ===================================
  const equipmentTemplates = ref([]);
  const deviceTemplates = ref([]);
  const zoneTemplates = computed(() =>
    equipmentTemplates.value.filter((t) => t.brick_class === 'AHU_Zone_Section'),
  );
  const controllerTemplates = ref([]);
  const availableDdcPanels = ref([]);

  const ddcControllerToAdd = ref({
    template_id: null,
    quantity: 1,
    position_label: '',
  });

  // ===================================
  // LOCATION PICKER STATE
  // ===================================
  const deviceLocationPath = ref([]);
  const selectedLocationNode = ref(null);
  const selectedLocationForPicker = ref(null);
  const locationPickerMode = ref('add'); // 'add' or 'edit'

  // ===================================
  // POINT TYPE OPTIONS
  // ===================================
  const pointTypeOptions = [
    { label: 'Virtual', value: 'Virtual' },
    { label: 'Sensor', value: 'Sensor' },
    { label: 'Setpoint', value: 'Setpoint' },
    { label: 'Command', value: 'Command' },
    { label: 'Status', value: 'Status' },
    { label: 'Alarm', value: 'Alarm' },
  ];

  // ===================================
  // COMPUTED PROPERTIES
  // ===================================
  const spaceOptions = computed(() => {
    if (!props.localHierarchyData?.buildings) {
      return [];
    }

    // Optimized: Use flatMap to reduce iterations and intermediate arrays
    return props.localHierarchyData.buildings.flatMap((building) =>
      (building.levels || []).flatMap((level) =>
        (level.spaces || []).map((space) => ({
          label: `${building.name} > ${level.name} > ${space.name}`,
          value: space.id,
          space_name: space.name,
          building_name: building.name,
          level_name: level.name,
        })),
      ),
    );
  });

  const availableSpacesForZone = computed(() => {
    if (!props.localHierarchyData?.buildings) {
      return [];
    }

    // Optimized: Use flatMap to reduce iterations and intermediate arrays
    return props.localHierarchyData.buildings.flatMap((building) =>
      (building.levels || []).flatMap((level) =>
        (level.spaces || []).map((space) => ({
          label: `${building.name} > ${level.name} > ${space.name}`,
          value: space.id,
        })),
      ),
    );
  });

  const equipmentTemplateOptions = computed(() => {
    if (!equipmentTemplates.value.length) {return [];}

    return equipmentTemplates.value.map((template) => ({
      label: template.name,
      value: template.id,
      description: template.description,
      brick_class: template.brick_class,
      point_count: template.point_count || 0,
      ...template,
    }));
  });

  const allTemplateOptions = computed(() => {
    return [
      ...deviceTemplates.value.map((t) => ({
        ...t,
        type: 'simple',
      })),
    ];
  });

  const locationTreeNodes = computed(() => {
    // Build location tree from hierarchy data
    if (!props.localHierarchyData || !props.localHierarchyData.buildings) {
      return [];
    }

    return props.localHierarchyData.buildings.map((building) => ({
      id: `building-${building.id}`,
      label: building.name,
      icon: 'business',
      type: 'building',
      data: building,
      children: building.levels?.map((level) => ({
        id: `level-${level.id}`,
        label: level.name,
        icon: 'layers',
        type: 'level',
        data: level,
        children: level.spaces?.map((space) => ({
          id: `space-${space.id}`,
          label: space.name,
          icon: 'room',
          type: 'space',
          data: space,
          selectable: true,
        })) || [],
      })) || [],
    }));
  });

  const powerSourceOptions = computed(() => {
    // Get available power sources from equipment list
    const sources = [];

    props.equipmentList.forEach((eq) => {
      if (eq.brick_class && (
        eq.brick_class.includes('Main_Switchboard') ||
        eq.brick_class.includes('Mechanical_Services_Switchboard') ||
        eq.brick_class.includes('Distribution_Board')
      )) {
        let type = 'Switchboard';
        let badge = 'SB';
        let color = 'grey';

        if (eq.brick_class.includes('Main_Switchboard')) {
          type = 'Main Switchboard';
          badge = 'MSB';
          color = 'red';
        } else if (eq.brick_class.includes('Mechanical_Services_Switchboard')) {
          type = 'Mechanical Services Switchboard';
          badge = 'MSSB';
          color = 'orange';
        } else if (eq.brick_class.includes('Distribution_Board')) {
          type = 'Distribution Board';
          badge = 'DB';
          color = 'blue';
        }

        sources.push({
          label: eq.name,
          value: eq.id,
          type,
          badge,
          color,
        });
      }
    });

    return sources;
  });

  // ===================================
  // EQUIPMENT OPERATIONS
  // ===================================

  // Show add equipment dialog
  function showAddEquipmentDialogForNode(node) {
    if (node.type === 'zoneSection' || (node.data && node.data.brick_class === 'AHU_Zone_Section')) {
      showAddDeviceDialogForNode(node);
      return;
    }

    selectedSpace.value = node.data;
    selectedEquipment.value = null;
    newEquipment.value = {
      name: '',
      brick_class: '',
      description: '',
      equipment_template_id: null,
      selectedTemplate: null,
      space_id: node.data?.id || null,
      parent_equipment_id: null,
      serves_space: null,
    };

    showEquipmentDialog.value = true;
    loadEquipmentTemplates();
  }

  // Add equipment
  async function addEquipment() {
    if (!newEquipment.value.name || !newEquipment.value.brick_class) {
      $q.notify({
        type: 'negative',
        message: 'Equipment name and type are required',
      });
      return;
    }

    saving.value = true;
    try {
      const equipmentData = {
        ...newEquipment.value,
        space_id: newEquipment.value.space_id || selectedSpace.value?.id,
      };

      // Check if we're in quote context
      if (props.quoteContext?.quote_id) {
        // Map equipment_template_id to template_id for quote API
        const quoteEquipmentData = {
          ...equipmentData,
          template_id: equipmentData.equipment_template_id,
        };
        // Use quote-specific API endpoint
        await quoteService.createQuoteEquipment(
          props.quoteContext.quote_id,
          quoteEquipmentData,
        );

        showEquipmentDialog.value = false;
        resetEquipmentForm();

        $q.notify({
          type: 'positive',
          message: `Equipment "${equipmentData.name}" added to quote`,
        });

        emit('equipment-added');
        emit('refresh-data');
      } else {
        // Use site-specific API endpoint
        await equipmentApi.createEquipment(equipmentData);

        showEquipmentDialog.value = false;
        resetEquipmentForm();

        $q.notify({
          type: 'positive',
          message: 'Equipment added successfully',
        });

        emit('equipment-added');
        emit('refresh-data');
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to add equipment: ${error.message}`,
      });
    } finally {
      saving.value = false;
    }
  }

  // Edit equipment
  function editEquipmentNode(node) {
    if (isTemplatedEquipment(node) && !isZoneSection(node) && !isController(node)) {
      $q.notify({
        type: 'warning',
        message: 'Templated equipment can only be edited through the template manager',
        icon: 'warning',
      });
      return;
    }

    const equipmentData = node.data || node;
    editingEquipment.value = {
      id: equipmentData.id,
      name: equipmentData.name || node.label,
      description: equipmentData.description || '',
      brick_class: equipmentData.brick_class || '',
      serves_space: null,
      controller_id: equipmentData.controller?.id || null,
      power_source_id: equipmentData.power_source?.id || null,
      power_classification: equipmentData.metadata?.power_classification || null,
      ddcControllers: [],
    };

    loadAvailableDdcPanels();

    if (equipmentData.brick_class && equipmentData.brick_class.includes('DDC_Panel')) {
      loadControllerTemplates();
      loadEquipmentDdcControllers(equipmentData.id);
    }

    showEditEquipmentDialog.value = true;
  }

  // Update equipment
  async function updateEquipment() {
    saving.value = true;
    try {
      await equipmentApi.updateEquipment(editingEquipment.value.id, {
        name: editingEquipment.value.name,
        description: editingEquipment.value.description,
        brick_class: editingEquipment.value.brick_class,
        serves_space: editingEquipment.value.serves_space,
        controller_id: editingEquipment.value.controller_id,
        power_source_id: editingEquipment.value.power_source_id,
        metadata: {
          power_classification: editingEquipment.value.power_classification,
        },
        ddc_controllers: editingEquipment.value.ddcControllers,
      });

      showEditEquipmentDialog.value = false;

      $q.notify({
        type: 'positive',
        message: 'Equipment updated successfully',
      });

      emit('equipment-updated');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update equipment: ${error.message}`,
      });
    } finally {
      saving.value = false;
    }
  }

  // Delete equipment
  function deleteEquipment(node) {
    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete equipment "${node.label}"? This will also delete all devices and points.`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
      },
    }).onOk(async () => {
      try {
        await equipmentApi.deleteEquipment(node.data.id);

        $q.notify({
          type: 'positive',
          message: 'Equipment deleted successfully',
        });

        emit('equipment-deleted');
        emit('refresh-data');
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: `Failed to delete equipment: ${error.message}`,
        });
      }
    });
  }

  // ===================================
  // DEVICE OPERATIONS
  // ===================================

  // Show add device dialog
  async function showAddDeviceDialogForNode(node) {
    if (isTemplatedEquipment(node)) {
      $q.notify({
        type: 'warning',
        message: 'This equipment was created from a template and cannot be modified',
        caption: 'Devices for templated equipment are managed through the template',
        icon: 'warning',
        timeout: 5000,
      });
      return;
    }

    selectedEquipment.value = {
      ...node.data,
      type: node.type,
    };

    newDevice.value = {
      name: '',
      brick_class: '',
      manufacturer: '',
      model_number: '',
      device_template_id: null,
      properties: {},
      location_space_id: null,
      location_description: '',
    };

    if (selectedEquipment.value && selectedEquipment.value.space_id) {
      newDevice.value.location_space_id = selectedEquipment.value.space_id;
      buildLocationPath(selectedEquipment.value.space_id);
    } else {
      deviceLocationPath.value = [];
    }

    await loadDeviceTemplates();
    showDeviceDialog.value = true;
  }

  // Add device
  async function addDevice() {
    if (!newDevice.value.name || !newDevice.value.brick_class) {
      $q.notify({
        type: 'negative',
        message: 'Device name and type are required',
      });
      return;
    }

    saving.value = true;
    try {
      const deviceData = {
        equipment_id: selectedEquipment.value.id,
        name: newDevice.value.name,
        brick_class: newDevice.value.brick_class,
        device_part_id: newDevice.value.device_part_id || null,
        device_template_id: newDevice.value.device_template_id || null,
        properties: newDevice.value.properties || {},
        location_space_id: newDevice.value.location_space_id || null,
        location_description: newDevice.value.location_description || null,
      };

      // Add quote context if in quote mode
      if (props.quoteContext?.quote_id) {
        deviceData.quote_id = props.quoteContext.quote_id;
        deviceData.lifecycle_stage = 'quote';
      }

      await deviceApi.createDevice(deviceData);

      showDeviceDialog.value = false;
      resetDeviceForm();

      $q.notify({
        type: 'positive',
        message: 'Device added successfully',
      });

      emit('device-added');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to add device: ${error.message}`,
      });
    } finally {
      saving.value = false;
    }
  }

  // Edit device - fetches fresh data from API to ensure device_part and location are available
  async function editDevice(node) {
    if (isTemplatedDevice(node)) {
      $q.notify({
        type: 'warning',
        message: 'Templated devices can only be edited through the template manager',
        icon: 'warning',
      });
      return;
    }

    const deviceData = node.data || node;
    const deviceId = deviceData.id;

    // Fetch fresh device data from API to ensure we have device_part and location_space
    // Tree node data may not include these fields due to caching or partial data loading
    const freshData = await deviceApi.getDevice(deviceId);

    if (freshData) {
      // Use fresh API data for the edit form
      editingDevice.value = {
        id: freshData.id,
        name: freshData.name || node.label,
        brick_class: freshData.brick_class || '',
        serial_number: freshData.serial_number || '',
        device_part_id: freshData.device_part?.id || freshData.device_part_id || null,
        device_part: freshData.device_part || null,
        properties: freshData.properties || {},
        location_space_id: freshData.location_space_id || null,
        location_description: freshData.location_description || '',
      };

      // Store template's brick_class constraint for subclass filtering
      // If device has a template, only allow its brick_class or subclasses
      editingDeviceTemplateConstraint.value = freshData.template?.brick_class || null;

      if (freshData.location_space_id) {
        buildLocationPath(freshData.location_space_id);
      } else {
        deviceLocationPath.value = [];
      }
    } else {
      // Fallback to node data if API call fails
      editingDevice.value = {
        id: deviceData.id,
        name: deviceData.name || node.label,
        brick_class: deviceData.brick_class || '',
        serial_number: deviceData.serial_number || '',
        device_part_id: deviceData.device_part?.id || null,
        device_part: deviceData.device_part || null,
        properties: deviceData.properties || {},
        location_space_id: deviceData.location_space_id || null,
        location_description: deviceData.location_description || '',
      };

      // Clear template constraint for fallback - no filtering
      editingDeviceTemplateConstraint.value = null;

      if (deviceData.location_space_id) {
        buildLocationPath(deviceData.location_space_id);
      } else {
        deviceLocationPath.value = [];
      }
    }

    showEditDeviceDialog.value = true;
  }

  // Update device
  async function updateDevice() {
    saving.value = true;
    try {
      await deviceApi.updateDevice(editingDevice.value.id, {
        name: editingDevice.value.name,
        brick_class: editingDevice.value.brick_class,
        serial_number: editingDevice.value.serial_number,
        device_part_id: editingDevice.value.device_part_id,
        properties: editingDevice.value.properties,
        location_space_id: editingDevice.value.location_space_id,
        location_description: editingDevice.value.location_description,
      });

      showEditDeviceDialog.value = false;

      $q.notify({
        type: 'positive',
        message: 'Device updated successfully',
      });

      emit('device-updated');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update device: ${error.message}`,
      });
    } finally {
      saving.value = false;
    }
  }

  // Delete device
  function deleteDevice(node) {
    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete device "${node.label}"?`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
      },
    }).onOk(async () => {
      try {
        await deviceApi.deleteDevice(node.data.id);

        $q.notify({
          type: 'positive',
          message: 'Device deleted successfully',
        });

        emit('device-deleted');
        emit('refresh-data');
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: `Failed to delete device: ${error.message}`,
        });
      }
    });
  }

  // ===================================
  // POINT OPERATIONS
  // ===================================

  // Show add point dialog
  function showAddPointDialogForNode(node) {
    selectedEquipment.value = {
      ...node.data,
      type: node.type,
    };

    newPoint.value = {
      name: '',
      brick_class: '',
      point_type: 'Virtual',
      unit_mapping_id: null,
    };

    showPointDialog.value = true;
  }

  // Add point
  async function addPoint() {
    if (!newPoint.value.name || !newPoint.value.brick_class) {
      $q.notify({
        type: 'negative',
        message: 'Point name and type are required',
      });
      return;
    }

    saving.value = true;
    try {
      // Determine if we're adding to equipment or device based on the selected node type
      const isDevice = selectedEquipment.value.type === 'device' ||
        selectedEquipment.value.equipment_id !== undefined;

      const pointData = {
        name: newPoint.value.name,
        brick_class: newPoint.value.brick_class,
        point_type: newPoint.value.point_type,
        unit_mapping_id: newPoint.value.unit_mapping_id,
      };

      // Set the correct foreign key based on whether it's a device or equipment
      if (isDevice) {
        pointData.device_id = selectedEquipment.value.id;
      } else {
        pointData.equipment_id = selectedEquipment.value.id;
      }

      await pointApi.createPoint(pointData);

      showPointDialog.value = false;
      resetPointForm();

      $q.notify({
        type: 'positive',
        message: 'Point added successfully',
      });

      emit('point-added');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to add point: ${error.message}`,
      });
    } finally {
      saving.value = false;
    }
  }

  // Edit point
  function editPoint(node) {
    const pointData = node.data || node;
    editingPoint.value = {
      id: pointData.id,
      name: pointData.name || node.label,
      brick_class: pointData.brick_class || '',
      point_type: pointData.point_type || 'Virtual',
      unit_mapping_id: pointData.unit_mapping_id || null,
    };
    showEditPointDialog.value = true;
  }

  // Update point
  async function updatePoint() {
    saving.value = true;
    try {
      await pointApi.updatePoint(editingPoint.value.id, {
        name: editingPoint.value.name,
        brick_class: editingPoint.value.brick_class,
        point_type: editingPoint.value.point_type,
        unit_mapping_id: editingPoint.value.unit_mapping_id,
      });

      showEditPointDialog.value = false;

      $q.notify({
        type: 'positive',
        message: 'Point updated successfully',
      });

      emit('point-updated');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update point: ${error.message}`,
      });
    } finally {
      saving.value = false;
    }
  }

  // Delete point
  function deletePoint(node) {
    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete point "${node.label}"?`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
      },
    }).onOk(async () => {
      try {
        await pointApi.deletePoint(node.data.id);

        $q.notify({
          type: 'positive',
          message: 'Point deleted successfully',
        });

        emit('point-deleted');
        emit('refresh-data');
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: `Failed to delete point: ${error.message}`,
        });
      }
    });
  }

  // ===================================
  // ZONE SECTION OPERATIONS
  // ===================================

  // Show add zone section dialog
  function showAddZoneSectionDialogForNode(node) {
    selectedAHU.value = node.data;
    selectedZoneTemplate.value = null;
    newZoneSection.value = {
      name: '',
      zone_type: 'mixing',
      serves_space: null,
      brick_class: 'AHU_Zone_Section',
    };
    showZoneSectionDialog.value = true;
  }

  // Apply zone template
  function applyZoneTemplate(template) {
    if (template) {
      newZoneSection.value.name = template.name;
      selectedZoneTemplate.value = template.id;
    }
  }

  // Reset zone section dialog
  function resetZoneSectionDialog() {
    selectedZoneTemplate.value = null;
    newZoneSection.value = {
      name: '',
      zone_type: 'mixing',
      serves_space: null,
      brick_class: 'AHU_Zone_Section',
    };
  }

  // Add zone section
  async function addZoneSection() {
    if (!newZoneSection.value.name) {
      $q.notify({
        type: 'negative',
        message: 'Zone section name is required',
      });
      return;
    }

    saving.value = true;
    try {
      await equipmentApi.createZoneSection(selectedAHU.value.id, {
        name: newZoneSection.value.name,
        zone_type: newZoneSection.value.zone_type,
        serves_space: newZoneSection.value.serves_space,
        equipment_template_id: selectedZoneTemplate.value,
      });

      showZoneSectionDialog.value = false;
      resetZoneSectionDialog();

      $q.notify({
        type: 'positive',
        message: 'Zone section added successfully',
      });

      emit('zone-section-added');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to add zone section: ${error.message}`,
      });
    } finally {
      saving.value = false;
    }
  }

  // ===================================
  // TEMPLATE LOADING
  // ===================================

  async function loadEquipmentTemplates() {
    loadingTemplates.value = true;
    try {
      const templates = await templateApi.loadEquipmentTemplates();
      equipmentTemplates.value = templates || [];
    } catch (error) {
      equipmentTemplates.value = [];
    } finally {
      loadingTemplates.value = false;
    }
  }

  async function loadDeviceTemplates() {
    try {
      const response = await authenticatedFetch('/api/v2/device-templates');
      if (response.ok) {
        const data = await response.json();
        deviceTemplates.value = data.data?.map((template) => ({
          label: `${template.name} (${template.manufacturer || 'Generic'})`,
          value: template.id,
          type: 'simple',
          description: template.description || `${template.brick_class || 'Device'} template`,
          brick_class: template.brick_class,
          manufacturer: template.manufacturer,
          model_number: template.model_number,
        })) || [];
      }
    } catch (error) {
      deviceTemplates.value = [];
    }
  }

  async function loadControllerTemplates() {
    try {
      const response = await authenticatedFetch('/api/v2/controller-templates');
      if (response.ok) {
        const data = await response.json();
        controllerTemplates.value = data.data || [];
      }
    } catch (error) {
      controllerTemplates.value = [];
    }
  }

  async function loadAvailableDdcPanels() {
    try {
      const response = await authenticatedFetch(`/api/v2/sites/${props.siteId}/equipment?brick_class=brick:DDC_Panel`);
      if (response.ok) {
        const data = await response.json();
        availableDdcPanels.value = data.data || [];
      }
    } catch (error) {
      availableDdcPanels.value = [];
    }
  }

  async function loadEquipmentDdcControllers(equipmentId) {
    try {
      const response = await authenticatedFetch(`/api/v2/sites/${props.siteId}/equipment/${equipmentId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.data && data.data.controller_templates) {
          editingEquipment.value.ddcControllers = data.data.controller_templates;
        }
      }
    } catch (error) {
    // Silently handle error
    }
  }

  // ===================================
  // DDC CONTROLLER MANAGEMENT
  // ===================================

  function addControllerToDdc() {
    if (!ddcControllerToAdd.value.template_id) {
      $q.notify({
        type: 'warning',
        message: 'Please select a controller template',
      });
      return;
    }

    const template = controllerTemplates.value.find((t) => t.id === ddcControllerToAdd.value.template_id);
    if (!template) {
      $q.notify({
        type: 'warning',
        message: 'Template not found',
      });
      return;
    }

    const controllerWithPivot = {
      ...template,
      pivot: {
        quantity: ddcControllerToAdd.value.quantity || 1,
        position_label: ddcControllerToAdd.value.position_label || null,
      },
    };

    if (!editingEquipment.value.ddcControllers) {
      editingEquipment.value.ddcControllers = [];
    }

    editingEquipment.value.ddcControllers.push(controllerWithPivot);

    ddcControllerToAdd.value = {
      template_id: null,
      quantity: 1,
      position_label: '',
    };

    $q.notify({
      type: 'positive',
      message: 'Controller added to panel',
    });
  }

  function removeControllerFromDdc(index) {
    editingEquipment.value.ddcControllers.splice(index, 1);

    $q.notify({
      type: 'info',
      message: 'Controller removed from panel',
    });
  }

  function calculateTotalIoForDdc() {
    if (!editingEquipment.value.ddcControllers || editingEquipment.value.ddcControllers.length === 0) {
      return 0;
    }

    return editingEquipment.value.ddcControllers.reduce((total, ctrl) => {
      const ioPoints = ctrl.total_io_points || 0;
      const quantity = ctrl.pivot?.quantity || 1;
      return total + (ioPoints * quantity);
    }, 0);
  }

  // ===================================
  // TEMPLATE SELECTION HANDLERS
  // ===================================

  function onEquipmentTemplateSelect(template) {
    if (!template) {
      newEquipment.value.selectedTemplate = null;
      newEquipment.value.equipment_template_id = null;
      return;
    }

    newEquipment.value.selectedTemplate = template;
    newEquipment.value.equipment_template_id = template.value;
    newEquipment.value.brick_class = template.brick_class;

    if (!newEquipment.value.name) {
      newEquipment.value.name = template.label;
    }
  }

  function onDeviceTemplateSelect(template) {
    if (!template) {
      newDevice.value.selectedTemplate = null;
      newDevice.value.device_template_id = null;
      return;
    }

    newDevice.value.selectedTemplate = template;
    newDevice.value.device_template_id = template.value;
    newDevice.value.brick_class = template.brick_class;
    newDevice.value.manufacturer = template.manufacturer || '';
    newDevice.value.model_number = template.model_number || '';

    if (!newDevice.value.name) {
      newDevice.value.name = template.label?.split(' (')[0] || '';
    }
  }

  function onEditDeviceTemplateSelect(template) {
    if (!template) {return;}

    editingDevice.value.brick_class = template.brick_class;
  }

  // ===================================
  // LOCATION PICKER
  // ===================================

  function openLocationPicker(mode = 'add') {
    locationPickerMode.value = mode;
    selectedLocationNode.value = null;

    if (mode === 'add' && newDevice.value.location_space_id) {
      selectedLocationForPicker.value = `space-${newDevice.value.location_space_id}`;
    } else if (mode === 'edit' && editingDevice.value.location_space_id) {
      selectedLocationForPicker.value = `space-${editingDevice.value.location_space_id}`;
    } else {
      selectedLocationForPicker.value = null;
    }

    showLocationPicker.value = true;
  }

  function confirmLocationSelection() {
    if (!selectedLocationNode.value) {
      $q.notify({
        type: 'warning',
        message: 'Please select a location',
      });
      return;
    }

    // Find the actual node object from the tree
    // selectedLocationNode.value is a string like "space-123", not an object
    const findNodeById = (nodes, targetId) => {
      for (const node of nodes) {
        if (node.id === targetId) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = findNodeById(node.children, targetId);
          if (found) {return found;}
        }
      }
      return null;
    };

    const selectedNode = findNodeById(locationTreeNodes.value, selectedLocationNode.value);
    if (!selectedNode || !selectedNode.data || !selectedNode.data.id) {
      $q.notify({
        type: 'warning',
        message: 'Invalid location selected',
      });
      return;
    }

    const spaceId = selectedNode.data.id;

    if (locationPickerMode.value === 'add') {
      newDevice.value.location_space_id = spaceId;
    } else {
      editingDevice.value.location_space_id = spaceId;
    }

    buildLocationPath(spaceId);
    showLocationPicker.value = false;
  }

  function clearDeviceLocation(mode = 'add') {
    if (mode === 'add') {
      newDevice.value.location_space_id = null;
    } else {
      editingDevice.value.location_space_id = null;
    }
    deviceLocationPath.value = [];
  }

  function buildLocationPath(spaceId) {
    if (!spaceId || !props.localHierarchyData?.buildings) {
      deviceLocationPath.value = [];
      return;
    }

    const path = [];

    for (const building of props.localHierarchyData.buildings) {
      for (const level of building.levels || []) {
        for (const space of level.spaces || []) {
          if (space.id === spaceId) {
            path.push({ label: building.name, icon: 'business' });
            path.push({ label: level.name, icon: 'layers' });
            path.push({ label: space.name, icon: 'room' });
            deviceLocationPath.value = path;
            return;
          }
        }
      }
    }

    deviceLocationPath.value = [];
  }

  function getSelectedLocationLabel() {
    if (deviceLocationPath.value.length === 0) {
      return 'No location selected';
    }
    return deviceLocationPath.value.map((item) => item.label).join(' > ');
  }

  // ===================================
  // HELPER FUNCTIONS
  // ===================================

  function isZoneSection(node) {
    return node.type === 'zoneSection' ||
      (node.data && node.data.brick_class === 'AHU_Zone_Section');
  }

  function isController(node) {
    const brickClass = node.data?.brick_class || '';
    return brickClass.includes('Controller') ||
      brickClass.includes('DDC') ||
      node.data?.controller_template_id;
  }

  // ===================================
  // FORM RESET FUNCTIONS
  // ===================================

  function resetEquipmentForm() {
    newEquipment.value = {
      name: '',
      brick_class: '',
      description: '',
      equipment_template_id: null,
      selectedTemplate: null,
      space_id: null,
      parent_equipment_id: null,
      serves_space: null,
    };
  }

  function resetDeviceForm() {
    newDevice.value = {
      name: '',
      brick_class: '',
      device_part_id: null,
      device_template_id: null,
      selectedTemplate: null,
      properties: {},
      location_space_id: null,
      location_description: '',
    };
    deviceLocationPath.value = [];
  }

  function resetPointForm() {
    newPoint.value = {
      name: '',
      brick_class: '',
      point_type: 'Virtual',
      unit_mapping_id: null,
    };
  }

  // ===================================
  // EXPOSE PUBLIC METHODS
  // ===================================
  defineExpose({
    // Equipment operations
    showAddEquipmentDialogForNode,
    editEquipment: editEquipmentNode,
    deleteEquipment,

    // Device operations
    showAddDeviceDialogForNode,
    editDevice,
    deleteDevice,

    // Point operations
    showAddPointDialogForNode,
    editPoint,
    deletePoint,

    // Zone section operations
    showAddZoneSectionDialogForNode,
  });
</script>

<style scoped>
.equipment-manager {
  /* Container styles - invisible wrapper */
}
</style>
